import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Investment, InvestmentPlatform, BankAccount } from "../types";
import { investmentsAPI, investmentPlatformsAPI, accountsAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";
import { 
  ArrowLeft, 
  Plus, 
  Edit2, 
  Trash2, 
  TrendingUp, 
  Calendar,
  DollarSign
} from "lucide-react";

const PlatformInvestments: React.FC = () => {
  const { platformId } = useParams<{ platformId: string }>();
  const [platform, setPlatform] = useState<InvestmentPlatform | null>(null);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);
  const [selectedType, setSelectedType] = useState<string>("all");
  const [withdrawingInvestment, setWithdrawingInvestment] = useState<Investment | null>(null);
  const [withdrawalData, setWithdrawalData] = useState({
    amount: "",
    bankAccount: "",
    currentPrice: "",
  });
  
  const [formData, setFormData] = useState({
    type: "stocks" as "stocks" | "fno" | "mutual_funds" | "bonds" | "fixed_deposits" | "crypto" | "etf",
    companyName: "",
    amount: "",
    quantity: "",
    purchasePrice: "",
    purchaseDate: new Date().toISOString().split('T')[0],
    maturityDate: "",
    interestRate: "",
    notes: "",
    paymentSource: "platform" as "platform" | "bank",
    bankAccount: "",
  });

  const fetchData = useCallback(async () => {
    if (!platformId) return;

    try {
      setLoading(true);
      const [platformData, investmentsData, accountsData] = await Promise.all([
        investmentPlatformsAPI.getPlatform(platformId),
        investmentsAPI.getInvestmentsByPlatform(platformId),
        accountsAPI.getAccounts(),
      ]);
      
      setPlatform(platformData);
      setInvestments(investmentsData);
      setBankAccounts(accountsData);
      
      // Set default bank account
      if (accountsData.length > 0 && !formData.bankAccount) {
        setFormData(prev => ({ ...prev, bankAccount: accountsData[0]._id }));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [platformId, formData.bankAccount]);

  useEffect(() => {
    if (platformId) {
      fetchData();
    }
  }, [platformId, fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.companyName.trim() || !formData.amount || !formData.purchaseDate) {
      setError("Company name, amount, and purchase date are required");
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    // Check balance based on payment source
    if (formData.paymentSource === "bank" && formData.bankAccount) {
      const selectedAccount = bankAccounts.find(acc => acc._id === formData.bankAccount);
      if (selectedAccount && selectedAccount.balance < amount) {
        setError(`Insufficient balance in ${selectedAccount.bankName}. Available: ₹${selectedAccount.balance.toLocaleString()}`);
        return;
      }
    } else if (formData.paymentSource === "platform" && platform) {
      if (platform.walletBalance < amount) {
        setError(`Insufficient platform wallet balance. Available: ₹${platform.walletBalance.toLocaleString()}`);
        return;
      }
    }

    try {
      setError("");
      const data: any = {
        platform: platformId,
        type: formData.type,
        companyName: formData.companyName.trim(),
        amount,
        purchaseDate: formData.purchaseDate,
        notes: formData.notes.trim(),
        deductFromPlatform: formData.paymentSource === "platform",
        deductFromBank: formData.paymentSource === "bank",
        bankAccount: formData.paymentSource === "bank" ? formData.bankAccount : undefined,
      };

      // Add optional fields based on investment type
      if (formData.quantity) data.quantity = parseFloat(formData.quantity);
      if (formData.purchasePrice) data.purchasePrice = parseFloat(formData.purchasePrice);
      if (formData.maturityDate) data.maturityDate = formData.maturityDate;
      if (formData.interestRate) data.interestRate = parseFloat(formData.interestRate);

      if (editingInvestment) {
        await investmentsAPI.updateInvestment(editingInvestment._id, data);
      } else {
        await investmentsAPI.createInvestment(data);
      }

      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save investment");
    }
  };

  const handleEdit = (investment: Investment) => {
    setEditingInvestment(investment);
    setFormData({
      type: investment.type,
      companyName: investment.companyName,
      amount: investment.amount.toString(),
      quantity: investment.quantity?.toString() || "",
      purchasePrice: investment.purchasePrice?.toString() || "",
      purchaseDate: investment.purchaseDate.split('T')[0],
      maturityDate: investment.maturityDate ? investment.maturityDate.split('T')[0] : "",
      interestRate: investment.interestRate?.toString() || "",
      notes: investment.notes || "",
      paymentSource: "platform",
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : "",
    });
    setShowAddForm(true);
  };

  const handleDelete = async (investmentId: string) => {
    if (window.confirm("Are you sure you want to delete this investment?")) {
      try {
        await investmentsAPI.deleteInvestment(investmentId);
        fetchData();
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to delete investment");
      }
    }
  };

  const handleWithdraw = (investment: Investment) => {
    setWithdrawingInvestment(investment);
    // For investments with quantity (stocks, etc.), don't pre-fill current price
    // User will enter it manually at sell time
    const hasQuantity = investment.quantity && investment.quantity > 0;
    
    setWithdrawalData({
      amount: hasQuantity ? "" : investment.amount.toString(),
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : "",
      currentPrice: "",
    });
  };

  const handleWithdrawSubmit = async () => {
    if (!withdrawingInvestment) return;

    // For investments with quantity, calculate based on entered current price
    const hasQuantity = withdrawingInvestment.quantity && withdrawingInvestment.quantity > 0;
    let amount: number;
    
    if (hasQuantity) {
      const currentPrice = parseFloat(withdrawalData.currentPrice);
      if (isNaN(currentPrice) || currentPrice <= 0) {
        setError("Please enter a valid current market price");
        return;
      }
      amount = withdrawingInvestment.quantity! * currentPrice;
    } else {
      amount = parseFloat(withdrawalData.amount);
      if (isNaN(amount) || amount <= 0) {
        setError("Please enter a valid withdrawal amount");
        return;
      }
    }

    if (!withdrawalData.bankAccount) {
      setError("Please select a bank account");
      return;
    }

    try {
      setError("");
      await investmentsAPI.withdrawInvestment(withdrawingInvestment._id, {
        bankAccount: withdrawalData.bankAccount,
        withdrawalAmount: amount,
      });
      setWithdrawingInvestment(null);
      setWithdrawalData({ amount: "", bankAccount: "", currentPrice: "" });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to withdraw investment");
    }
  };

  const resetForm = () => {
    setFormData({
      type: "stocks",
      companyName: "",
      amount: "",
      quantity: "",
      purchasePrice: "",
      purchaseDate: new Date().toISOString().split('T')[0],
      maturityDate: "",
      interestRate: "",
      notes: "",
      paymentSource: "platform",
      bankAccount: bankAccounts.length > 0 ? bankAccounts[0]._id : "",
    });
    setShowAddForm(false);
    setEditingInvestment(null);
    setError("");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      stocks: "Stocks",
      fno: "F&O",
      mutual_funds: "Mutual Funds",
      bonds: "Bonds",
      fixed_deposits: "Fixed Deposits",
      crypto: "Crypto",
      etf: "ETF",
    };
    return labels[type] || type;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: "bg-green-100 text-green-700",
      sold: "bg-blue-100 text-blue-700",
      matured: "bg-purple-100 text-purple-700",
      withdrawn: "bg-gray-100 text-gray-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const filteredInvestments = selectedType === "all" 
    ? investments 
    : investments.filter(inv => inv.type === selectedType);

  const investmentTypes = [
    { value: "all", label: "All Types" },
    { value: "stocks", label: "Stocks" },
    { value: "fno", label: "F&O" },
    { value: "mutual_funds", label: "Mutual Funds" },
    { value: "bonds", label: "Bonds" },
    { value: "fixed_deposits", label: "Fixed Deposits" },
    { value: "crypto", label: "Crypto" },
    { value: "etf", label: "ETF" },
  ];

  if (loading) {
    return (
      <PageTransition>
        <div className="p-6 max-w-7xl mx-auto">
          <LoadingSkeleton variant="table" count={5} />
        </div>
      </PageTransition>
    );
  }

  if (!platform) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Platform not found</h3>
          <Link to="/investments/platforms" className="text-blue-600 hover:text-blue-700">
            Back to Platforms
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/investments/platforms"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Platforms
          </Link>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {platform.name} - Investments
              </h1>
              <p className="text-gray-600 mt-2">Manage your investments on this platform</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              Add Investment
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Withdrawal Modal */}
        {withdrawingInvestment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Sell/Withdraw Investment
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Selling: <span className="font-semibold">{withdrawingInvestment.companyName}</span>
              </p>
              
              {/* Show original investment info */}
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Original Investment:</span>
                  <span className="font-medium">{formatCurrency(withdrawingInvestment.amount)}</span>
                </div>
                {withdrawingInvestment.quantity && withdrawingInvestment.quantity > 0 && (
                  <>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Purchase Price:</span>
                      <span className="font-medium">
                        {formatCurrency(withdrawingInvestment.purchasePrice || 0)} × {withdrawingInvestment.quantity}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Quantity:</span>
                      <span className="font-medium">{withdrawingInvestment.quantity} units</span>
                    </div>
                  </>
                )}
              </div>
              
              {/* Current Price Input for quantity-based investments */}
              {withdrawingInvestment.quantity && withdrawingInvestment.quantity > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Market Price per Unit *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={withdrawalData.currentPrice}
                    onChange={(e) => {
                      const currentPrice = e.target.value;
                      const calculatedAmount = currentPrice && withdrawingInvestment.quantity
                        ? (parseFloat(currentPrice) * withdrawingInvestment.quantity).toString()
                        : "";
                      setWithdrawalData({ 
                        ...withdrawalData, 
                        currentPrice,
                        amount: calculatedAmount
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter current price per unit"
                    required
                  />
                  {withdrawalData.currentPrice && (
                    <p className="text-xs text-green-600 mt-1 font-medium">
                      Total Value: {formatCurrency(parseFloat(withdrawalData.currentPrice) * withdrawingInvestment.quantity)}
                    </p>
                  )}
                </div>
              )}
              
              {/* Withdrawal Amount (read-only for quantity-based, editable for others) */}
              {!(withdrawingInvestment.quantity && withdrawingInvestment.quantity > 0) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Withdrawal Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={withdrawingInvestment.amount}
                    value={withdrawalData.amount}
                    onChange={(e) => setWithdrawalData({ ...withdrawalData, amount: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Max: ${formatCurrency(withdrawingInvestment.amount)}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum: {formatCurrency(withdrawingInvestment.amount)}
                  </p>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deposit to Bank Account
                </label>
                <select
                  value={withdrawalData.bankAccount}
                  onChange={(e) => setWithdrawalData({ ...withdrawalData, bankAccount: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {bankAccounts.map((account) => (
                    <option key={account._id} value={account._id}>
                      {account.bankName} - {formatCurrency(account.balance)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleWithdrawSubmit}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors font-medium"
                >
                  Confirm Withdrawal
                </button>
                <button
                  onClick={() => {
                    setWithdrawingInvestment(null);
                    setWithdrawalData({ amount: "", bankAccount: "", currentPrice: "" });
                    setError("");
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingInvestment ? "Edit Investment" : "Add New Investment"}
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Bank Balance Info */}
                {bankAccounts.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Available Bank Balance</h4>
                    <div className="space-y-1">
                      {bankAccounts.map((account) => (
                        <div key={account._id} className="flex justify-between text-sm">
                          <span className="text-blue-800">{account.bankName}</span>
                          <span className="font-semibold text-blue-900">
                            {formatCurrency(account.balance)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Investment Type *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="stocks">Stocks</option>
                      <option value="fno">F&O</option>
                      <option value="mutual_funds">Mutual Funds</option>
                      <option value="bonds">Bonds</option>
                      <option value="fixed_deposits">Fixed Deposits</option>
                      <option value="crypto">Crypto</option>
                      <option value="etf">ETF</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Scheme Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Reliance Industries, HDFC Mutual Fund"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Investment Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Purchase Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Conditional fields for stocks */}
                  {(formData.type === "stocks" || formData.type === "fno") && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Number of shares"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Purchase Price per Share
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.purchasePrice}
                          onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Price per share"
                        />
                      </div>
                    </>
                  )}

                  {/* Conditional fields for mutual funds, bonds, FDs */}
                  {(formData.type === "mutual_funds" || formData.type === "bonds" || formData.type === "fixed_deposits") && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maturity Date
                        </label>
                        <input
                          type="date"
                          value={formData.maturityDate}
                          onChange={(e) => setFormData({ ...formData, maturityDate: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Interest Rate (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.interestRate}
                          onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Annual interest rate"
                        />
                      </div>
                    </>
                  )}

                  {/* Payment source selection */}
                  {!editingInvestment && (
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Payment Source
                      </label>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, paymentSource: "platform" })}
                          className={`px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                            formData.paymentSource === "platform"
                              ? "border-blue-600 bg-blue-50 text-blue-700"
                              : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <div className="text-sm mb-1">Platform Wallet</div>
                          {platform && (
                            <div className="text-xs font-normal">
                              Available: {formatCurrency(platform.walletBalance)}
                            </div>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, paymentSource: "bank" })}
                          className={`px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                            formData.paymentSource === "bank"
                              ? "border-blue-600 bg-blue-50 text-blue-700"
                              : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <div className="text-sm mb-1">Bank Account</div>
                          <div className="text-xs font-normal">
                            {bankAccounts.length > 0 ? `${bankAccounts.length} account(s)` : "No accounts"}
                          </div>
                        </button>
                      </div>

                      {formData.paymentSource === "bank" && bankAccounts.length > 0 && (
                        <select
                          value={formData.bankAccount}
                          onChange={(e) => setFormData({ ...formData, bankAccount: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {bankAccounts.map((account) => (
                            <option key={account._id} value={account._id}>
                              {account.bankName} - {formatCurrency(account.balance)} available
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes about this investment..."
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    {editingInvestment ? "Update Investment" : "Add Investment"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-2">
          <div className="flex flex-wrap gap-2">
            {investmentTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedType === type.value
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Investments Table */}
        {filteredInvestments.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No investments yet</h3>
            <p className="text-gray-600 mb-6">Start by adding your first investment</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
            >
              Add Investment
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedType === "all" ? "All Investments" : getTypeLabel(selectedType)} ({filteredInvestments.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company/Scheme
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maturity Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvestments.map((investment) => (
                    <tr key={investment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {investment.companyName}
                        </div>
                        {investment.notes && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {investment.notes}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                          {getTypeLabel(investment.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">
                          {formatCurrency(investment.amount)}
                        </div>
                        {investment.quantity && (
                          <div className="text-xs text-gray-500">
                            {investment.quantity} units @ {formatCurrency(investment.purchasePrice || 0)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          {new Date(investment.purchaseDate).toLocaleDateString('en-IN')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {investment.maturityDate ? (
                          <div className="text-sm text-gray-900">
                            {new Date(investment.maturityDate).toLocaleDateString('en-IN')}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(investment.status)}`}>
                          {investment.status.charAt(0).toUpperCase() + investment.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(investment)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {investment.status === "active" && (
                            <button
                              onClick={() => handleWithdraw(investment)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Withdraw"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(investment._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default PlatformInvestments;
